import { useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { useToast } from '../context/ToastContext'
import { useAsk } from '../context/PromptProvider'
import { useFormDraft } from '../hooks/useFormDraft'
import useFieldIds from '../hooks/useFieldIds'
import { usePageShortcut } from '../hooks/useKeyboardShortcuts'
import { InlineText, InlineSelect } from '../components/InlineEdit'
import { recentContactsTracker } from '../lib/recentItems'
import api, { listTags, linkContactTag, unlinkContactTag, bulkTagContacts } from '../services/api'
import { SkeletonTable, LoadingRegion } from '../components/Skeleton'
import EmptyState from '../components/EmptyState'
import { usePendingDelete } from '../hooks/usePendingDelete'
import useBulkSelect from '../hooks/useBulkSelect'
import BulkActionBar from '../components/BulkActionBar'
import useTableSort, { SortIcon } from '../hooks/useTableSort'
import useModal from '../hooks/useModal'
import HelpButton from '../components/HelpButton'
import CustomFieldsSection from '../components/CustomFieldsSection'
import TagChipsSection from '../components/TagChipsSection'
import BulkTagPicker from '../components/BulkTagPicker'
import { CONTACT_GROUPS, groupLabel, groupColor } from '../lib/contactGroups'

// Ship 6e — persisted per-user tag filter for the Contacts list.  AND
// semantic: a contact must carry ALL selected tags to appear.
const TAG_FILTER_KEY = 'lextrack_contacts_filter_tags'


const emptyForm = {
  first_name: '', middle_name: '', last_name: '', company_name: '',
  email: '', phone: '', secondary_phone: '', contact_group: 'other',
  address: '', city: '', state: '', zip: '', contact_date: '', notes: ''
}

export default function Contacts() {
  const { user } = useAuth()
  const toast = useToast()
  const ask = useAsk()
  const pending = usePendingDelete()
  const sort = useTableSort('contacts', { key: 'last_name', dir: 'asc' })
  // Group-change modal state — inline picker for bulk contact_group change.
  const [bulkGroupOpen, setBulkGroupOpen] = useState(false)
  const [bulkGroupValue, setBulkGroupValue] = useState('other')
  const [bulkTagOpen, setBulkTagOpen] = useState(false)
  const [bulkBusy, setBulkBusy] = useState(false)
  // M-Cx2: wire the bulk-group modal through useModal for Esc-close +
  // focus-trap + return-focus discipline (matches other modals in the app).
  const closeBulkGroup = () => { if (!bulkBusy) setBulkGroupOpen(false) }
  const { containerProps: bulkGroupContainerProps } = useModal({
    enabled: bulkGroupOpen,
    onClose: closeBulkGroup,
  })
  const [searchParams, setSearchParams] = useSearchParams()
  const [contacts, setContacts] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [showForm, setShowForm] = useState(false)
  const [editingContact, setEditingContact] = useState(null)
  const [search, setSearch] = useState('')
  // Server-side pagination — 100/page keeps the round-trip cost low but
  // still fits a full alphabet section on one screen so scrolling feels
  // continuous.
  const PAGE_SIZE = 100
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalCount, setTotalCount] = useState(0)

  // Page shortcuts — `n` opens the new-contact form; `f` focuses page search.
  usePageShortcut('n', () => { setEditingContact(null); setShowForm(true) })
  usePageShortcut('f', () => document.querySelector('input[placeholder*="Search contacts"]')?.focus())
  const [filterGroup, setFilterGroup] = useState('')
  // Ship 7e — client-side "only 1099 vendors" filter chip.  Backend
  // serializer returns is_1099_vendor for every caller (harmless boolean);
  // filter is applied after the paginated fetch.  Persisted per-user.
  const [only1099, setOnly1099] = useState(() => {
    try { return localStorage.getItem('lextrack_contacts_only_1099') === '1' }
    catch { return false }
  })
  useEffect(() => {
    try { localStorage.setItem('lextrack_contacts_only_1099', only1099 ? '1' : '0') } catch {}
  }, [only1099])

  // Ship 6e — tag filter state.  Persisted per-user, feeds the ?tag_ids=
  // backend filter (AND semantic).  allTags is the firm's tag catalog for
  // rendering both the filter chip row + edit-form TagChipsSection.
  const [tagFilterIds, setTagFilterIds] = useState(() => {
    try {
      const raw = localStorage.getItem(TAG_FILTER_KEY)
      const p = raw ? JSON.parse(raw) : []
      return Array.isArray(p) ? p.filter(Number.isInteger) : []
    } catch { return [] }
  })
  const [allTags, setAllTags] = useState([])
  useEffect(() => {
    let cancelled = false
    listTags()
      .then(r => { if (!cancelled) setAllTags(r.data?.tags || r.data || []) })
      .catch(() => { if (!cancelled) setAllTags([]) })
    return () => { cancelled = true }
  }, [])
  useEffect(() => {
    try { localStorage.setItem(TAG_FILTER_KEY, JSON.stringify(tagFilterIds)) } catch {}
  }, [tagFilterIds])
  const toggleTagFilter = (id) => setTagFilterIds(prev =>
    prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
  )
  const clearTagFilter = () => setTagFilterIds([])
  const [form, setForm] = useState({ ...emptyForm })
  const fid = useFieldIds()

  // Ship 7e — 1099 vendor state.  Kept separate from `form` because these
  // fields flow through the same contacts PUT but are strictly perm-gated;
  // isolating them avoids leaking values into the draft-save (which persists
  // to localStorage — TIN plaintext MUST NOT land in localStorage).
  // `tin` is initialized blank on every open (never pre-fill from server —
  // server only returns tin_masked).  Save only sends `tin` when the user
  // typed a new value; blank means "keep existing".
  const [vendorForm, setVendorForm] = useState({
    is_1099_vendor: false,
    vendor_name_override: '',
    tin_type: 'SSN',
    tin: '',
    tin_masked: '',
    w9_on_file: false,
    w9_received_at: '',
  })
  const [vendorSectionOpen, setVendorSectionOpen] = useState(false)

  const canCreate = (user?.role === 'admin' || user?.permissions?.is_admin) || user?.permissions?.can_create_contacts
  const canDelete = (user?.role === 'admin' || user?.permissions?.is_admin) || user?.permissions?.can_delete_contacts
  // Tagging is gated on can_edit_contacts — matching both the per-row
  // TagChipsSection below and POST /api/tags/contacts/bulk-tag on the server.
  // Deliberately NOT canCreate: creating a contact and re-labelling one are
  // different permissions.
  const canTagContacts = (user?.role === 'admin' || user?.permissions?.is_admin) || Boolean(user?.permissions?.can_edit_contacts)
  // Ship 7e — 1099 vendor fields are strictly gated on can_manage_accounting.
  // Server also refuses these fields in the contacts.js PUT body when the
  // caller lacks the perm (belt-and-suspenders); this hides the section.
  const canManage1099 = (user?.role === 'admin' || user?.permissions?.is_admin) || user?.permissions?.can_manage_accounting

  // Auto-save the create-contact draft — long forms are worth the safety net.
  const contactDraft = useFormDraft('lextrack_draft_contact_new', form, setForm, {
    enabled: showForm && !editingContact,
    initialForm: emptyForm,
    debounceMs: 3000,
  })

  // Inline field save — reuses the create/edit permission gate for authoring.
  const saveInlineField = async (row, field, value) => {
    try {
      const res = await api.put(`/api/contacts/${row.id}`, { ...row, [field]: value || null })
      setContacts((prev) => prev.map((c) => c.id === row.id ? { ...c, ...res.data } : c))
      toast.success('Contact updated')
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not save — reverted')
      throw err
    }
  }

  // Reset page to 1 whenever the search or group filter changes — otherwise
  // narrowing a filter on page 5 lands on an empty page.
  useEffect(() => { setPage(1) }, [search, filterGroup, tagFilterIds])

  // Debounce the search term so we don't fire one API call per keystroke.
  // 250ms is fast enough to feel live but coalesces bursts.
  const [debouncedSearch, setDebouncedSearch] = useState(search)
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 250)
    return () => clearTimeout(t)
  }, [search])

  // Track the in-flight fetch's AbortController so a stale response can't
  // overwrite a newer one when the user keeps typing.
  const inflightAbort = useRef(null)

  useEffect(() => { fetchContacts() /* eslint-disable-line */ }, [page, debouncedSearch, filterGroup, tagFilterIds])

  // ?new=1 from Ctrl+K "New contact"
  useEffect(() => {
    if (searchParams.get('new') === '1') {
      setShowForm(true)
      setSearchParams({}, { replace: true })
    }
  }, [searchParams, setSearchParams])

  // Ship 7b F2.2 — ?edit=<id> from ContactDuplicates side-Link deep-links into
  // the target contact's edit form.  Waits until contacts list is loaded so
  // we can hand the full row to handleEdit (which pre-fills every field +
  // preserves tag/group state).  Falls through silently if the id isn't in
  // the visible page (e.g. paginated away — no explicit warning to keep the
  // UX quiet for a low-value edge).
  useEffect(() => {
    const editId = parseInt(searchParams.get('edit') || '0', 10)
    if (!editId || contacts.length === 0) return
    const target = contacts.find((c) => c.id === editId)
    if (target) {
      handleEdit(target)
      setShowForm(true)
      setSearchParams({}, { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, contacts])

  const fetchContacts = async () => {
    setLoading(true)
    // Cancel any prior in-flight request so its late arrival can't clobber
    // this newer one (fixes the response-order race when typing fast).
    if (inflightAbort.current) {
      try { inflightAbort.current.abort() } catch { /* ignore */ }
    }
    const controller = new AbortController()
    inflightAbort.current = controller
    try {
      const params = { paginate: 'true', page, limit: PAGE_SIZE }
      if (debouncedSearch) params.q = debouncedSearch
      if (filterGroup) params.group = filterGroup
      // Ship 6e — tag AND-filter, comma-separated int list.
      if (tagFilterIds.length > 0) params.tag_ids = tagFilterIds.join(',')
      const res = await api.get('/api/contacts', { params, signal: controller.signal })
      // Guard: if a NEWER fetch has replaced us mid-flight, drop this result.
      if (inflightAbort.current !== controller) return
      // Paginated response is {items, total, page, pages}; guard against an
      // older shape (bare array) just in case a proxy caches something.
      if (Array.isArray(res.data)) {
        setContacts(res.data)
        setTotalPages(1)
        setTotalCount(res.data.length)
      } else {
        setContacts(res.data.items || [])
        setTotalPages(res.data.pages || 1)
        setTotalCount(res.data.total || 0)
      }
    } catch (err) {
      // Cancelled requests should NOT show an error banner.  axios surfaces
      // AbortController cancels as either err.name === 'CanceledError' or
      // err.code === 'ERR_CANCELED'; the fetch adapter uses AbortError.
      if (err?.name === 'CanceledError' || err?.name === 'AbortError' || err?.code === 'ERR_CANCELED') return
      setError(err.response?.data?.error || 'Error loading contacts')
    } finally {
      if (inflightAbort.current === controller) {
        inflightAbort.current = null
        setLoading(false)
      }
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    try {
      // Ship 7e — merge 1099 vendor fields into PUT body when editing +
      // caller has can_manage_accounting.  Never sent on POST (contact
      // must exist for TIN encryption + is_1099_vendor flip to apply
      // via server-authoritative setTinOnContact).  Server also strips
      // these fields from any caller lacking the perm.
      let body = form
      if (editingContact && canManage1099) {
        body = {
          ...form,
          is_1099_vendor: vendorForm.is_1099_vendor,
          vendor_name_override: vendorForm.vendor_name_override.trim() || null,
          tin_type: vendorForm.is_1099_vendor ? vendorForm.tin_type : null,
          w9_on_file: vendorForm.is_1099_vendor ? vendorForm.w9_on_file : false,
          w9_received_at: vendorForm.is_1099_vendor && vendorForm.w9_received_at
            ? vendorForm.w9_received_at
            : null,
        }
        // Only include plaintext `tin` when user typed a value in the
        // input.  Blank = keep existing ciphertext.  Server normalizes
        // digits + validates 9-digit format + encrypts + derives last4.
        if (vendorForm.is_1099_vendor && vendorForm.tin.trim() !== '') {
          body.tin = vendorForm.tin.trim()
        }
      }
      if (editingContact) {
        await api.put(`/api/contacts/${editingContact}`, body)
        setEditingContact(null)
      } else {
        await api.post('/api/contacts', form)
        contactDraft.clear()
      }
      setForm({ ...emptyForm })
      setVendorForm({
        is_1099_vendor: false, vendor_name_override: '', tin_type: 'SSN',
        tin: '', tin_masked: '', w9_on_file: false, w9_received_at: '',
      })
      setVendorSectionOpen(false)
      setShowForm(false)
      fetchContacts()
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.hint || 'Error saving contact')
    }
  }

  // Ship 6e — currently-edited contact's tags (mirrors the row's tags array).
  // Kept in local state so attach/detach re-renders immediately without a
  // full contacts refetch; refresh from the row on every handleEdit call.
  const [editingContactTags, setEditingContactTags] = useState([])

  const handleEdit = (contact) => {
    // Track for the sidebar "Recent Contacts" muscle-memory shortcut.
    // Contacts don't have a detail page — opening the edit form is the
    // "viewing this contact" event.
    const label = contact.company_name?.trim()
      || `${contact.first_name || ''} ${contact.last_name || ''}`.trim()
      || `Contact #${contact.id}`
    recentContactsTracker.push({ id: contact.id, title: label })
    setEditingContact(contact.id)
    setEditingContactTags(Array.isArray(contact.tags) ? contact.tags : [])
    setForm({
      first_name: contact.first_name || '',
      middle_name: contact.middle_name || '',
      last_name: contact.last_name || '',
      company_name: contact.company_name || '',
      email: contact.email || '',
      phone: contact.phone || '',
      secondary_phone: contact.secondary_phone || '',
      contact_group: contact.contact_group || 'other',
      address: contact.address || '',
      city: contact.city || '',
      state: contact.state || '',
      zip: contact.zip || '',
      contact_date: contact.contact_date ? contact.contact_date.slice(0, 10) : '',
      notes: contact.notes || ''
    })
    // Ship 7e — hydrate vendor fields from serializeContact response.
    // Server only returns these when caller has can_view_accounting;
    // missing = defaults.  TIN plaintext is NEVER pre-filled.
    setVendorForm({
      is_1099_vendor: !!contact.is_1099_vendor,
      vendor_name_override: contact.vendor_name_override || '',
      tin_type: contact.tin_type || 'SSN',
      tin: '',
      tin_masked: contact.tin_masked || '',
      w9_on_file: !!contact.w9_on_file,
      w9_received_at: contact.w9_received_at ? String(contact.w9_received_at).slice(0, 10) : '',
    })
    setVendorSectionOpen(!!contact.is_1099_vendor)
    setShowForm(true)
    window.scrollTo(0, 0)
  }

  const handleDelete = (id) => {
    // Undo-toast delete pattern — row disappears immediately, undo button
    // in the toast reverts the removal.  If the toast expires, the DELETE
    // fires against the API.
    const target = contacts.find(c => c.id === id)
    if (!target) return
    pending.schedule({
      url: `/api/contacts/${id}`,
      message: `Contact "${[target.first_name, target.last_name].filter(Boolean).join(' ') || 'contact'}" deleted`,
      onOptimisticRemove: () => {
        setContacts(prev => prev.filter(c => c.id !== id))
        recentContactsTracker.remove(id)
      },
      onRestore: () => setContacts(prev => [...prev, target].sort((a, b) => (a.last_name || '').localeCompare(b.last_name || ''))),
      onDeleteError: (err) => setError(err.response?.data?.error || 'Error deleting contact'),
    })
  }

  // Search + group filter are now applied server-side.  Client-side sort
  // still runs against the current page — small enough (100 rows) that
  // sort is instant, and matches the invoices/receipts pattern of "sort
  // within the visible page" (server ORDER BY is fixed to last_name).
  let filtered = sort.apply(contacts, {
    last_name: (c) => `${c.last_name || ''} ${c.first_name || ''}`.toLowerCase(),
    company: (c) => (c.company_name || '').toLowerCase(),
    email: (c) => (c.email || '').toLowerCase(),
    phone: (c) => c.phone || '',
    group: (c) => c.contact_group || '',
  })
  // Ship 7e — apply 1099-only chip after sort so ordering is stable.
  if (only1099) filtered = filtered.filter(c => c.is_1099_vendor)

  // Bulk selection on visible rows.  Auto-prunes when the visible set
  // changes (pagination or filter tweaks); selection resets naturally
  // when page/search/filter change since the page is refetched and rows
  // that scrolled out of the visible set are dropped from selection.
  const bulk = useBulkSelect(filtered, { rowKey: 'id' })

  // Bulk delete — iterate pending.schedule() per selected id so each row
  // gets its own 5-second undo window (matches per-row delete semantics).
  const handleBulkDelete = async () => {
    if (!canDelete || bulk.size === 0) return
    const confirmed = await ask({
      type: 'confirm',
      title: 'Delete selected contacts?',
      message: `About to delete ${bulk.size} contact${bulk.size === 1 ? '' : 's'}.  Each deletion has a 5-second undo window in the toast bar.`,
      confirmLabel: 'Delete',
      cancelLabel: 'Cancel',
      danger: true,
    })
    if (!confirmed) return
    const ids = Array.from(bulk.selectedIds)
    for (const id of ids) {
      const target = contacts.find(c => c.id === id)
      if (!target) continue
      pending.schedule({
        url: `/api/contacts/${id}`,
        message: `Contact "${[target.first_name, target.last_name].filter(Boolean).join(' ') || 'contact'}" deleted`,
        onOptimisticRemove: () => {
          setContacts(prev => prev.filter(c => c.id !== id))
          recentContactsTracker.remove(id)
        },
        onRestore: () => setContacts(prev => [...prev, target].sort((a, b) => (a.last_name || '').localeCompare(b.last_name || ''))),
        onDeleteError: (err) => setError(err.response?.data?.error || 'Error deleting contact'),
      })
    }
    bulk.clear()
  }

  // Bulk change contact_group — loops PUT /api/contacts/:id per selected
  // row (there's no bulk endpoint for contact_group; tags DO have one, see
  // handleBulkTag below).  Capped at 500 selections.
  // M-Cx8: collect per-row failure reasons so the summary tells the user
  // WHICH contacts failed + WHY, instead of a bare "N failed" count that
  // provides no forensic trail.
  const handleBulkChangeGroup = async () => {
    if (!canCreate || bulk.size === 0) return
    if (bulk.size > 500) {
      toast.error('Bulk group change capped at 500 contacts per operation.')
      return
    }
    setBulkBusy(true)
    const ids = Array.from(bulk.selectedIds)
    let ok = 0
    const failures = []
    for (const id of ids) {
      const target = contacts.find(c => c.id === id)
      if (!target) {
        failures.push({ id, name: `#${id}`, reason: 'not in current page' })
        continue
      }
      const labelForFail = [target.first_name, target.last_name].filter(Boolean).join(' ') || `#${id}`
      try {
        await api.put(`/api/contacts/${id}`, { ...target, contact_group: bulkGroupValue })
        ok++
      } catch (err) {
        const reason = err?.response?.data?.error
          || err?.response?.data?.hint
          || (err?.response?.status >= 500 ? 'server error' : `HTTP ${err?.response?.status || '?'}`)
        failures.push({ id, name: labelForFail, reason })
      }
    }
    setBulkBusy(false)
    setBulkGroupOpen(false)
    bulk.clear()
    fetchContacts()
    const fail = failures.length
    if (fail === 0) {
      toast.success(`Updated ${ok} contact${ok === 1 ? '' : 's'}`)
    } else {
      // Surface up to 5 failed contacts inline + total remainder count.
      const preview = failures.slice(0, 5).map(f => `${f.name} (${f.reason})`).join('; ')
      const suffix = fail > 5 ? ` and ${fail - 5} more` : ''
      setError(`Bulk group change: updated ${ok} of ${ok + fail}. Failed: ${preview}${suffix}.`)
      toast.warn(`Updated ${ok} of ${ok + fail} — ${fail} failed (see banner)`)
    }
  }

  // Bulk attach/detach of ONE tag.  Unlike the group change above this is a
  // SINGLE request — the server writes the whole selection in one statement,
  // so there's no per-row failure list to collect.
  const handleBulkTag = async (tag, mode) => {
    if (!canTagContacts || bulk.size === 0 || !tag) return
    const ids = Array.from(bulk.selectedIds)
    const attaching = mode !== 'detach'
    setBulkTagOpen(false)
    const confirmed = await ask({
      type: 'confirm',
      title: attaching ? 'Add tag to selected contacts?' : 'Remove tag from selected contacts?',
      message: `${attaching ? 'Add' : 'Remove'} the tag "${tag.name}" ${attaching ? 'to' : 'from'} ${ids.length} contact${ids.length === 1 ? '' : 's'}.`,
      confirmLabel: attaching ? 'Add tag' : 'Remove tag',
      cancelLabel: 'Cancel',
    })
    if (!confirmed) return
    setBulkBusy(true)
    try {
      const r = await bulkTagContacts({
        contact_ids: ids, tag_id: tag.id, mode: attaching ? 'attach' : 'detach',
      })
      const d = r?.data || {}
      const n = attaching ? (d.attached ?? 0) : (d.detached ?? 0)
      const already = Number.isFinite(d.skipped_already) ? d.skipped_already : 0
      const gone = Number.isFinite(d.skipped_out_of_scope) ? d.skipped_out_of_scope : 0
      const parts = []
      if (already > 0) parts.push(attaching ? `${already} already tagged` : `${already} weren't tagged`)
      if (gone > 0) parts.push(`${gone} no longer exist`)
      const suffix = parts.length ? ` (${parts.join(', ')})` : ''
      toast.success(`${attaching ? 'Tagged' : 'Untagged'} ${n} contact${n === 1 ? '' : 's'} — ${tag.name}${suffix}`)
      bulk.clear()
      fetchContacts()
    } catch (err) {
      const msg = err?.response?.data?.error || 'Bulk tag failed'
      setError(msg)
      toast.error(msg)
    } finally {
      setBulkBusy(false)
    }
  }

  if (loading) return (
    <LoadingRegion label="Loading contacts">
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
        <div className="h-8 w-40 bg-gray-100 rounded animate-pulse mb-4" />
        <SkeletonTable rows={8} cols={5} />
      </div>
    </LoadingRegion>
  )

  return (
    <div>
      {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 mb-4 text-sm">{error}</div>}

      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Contacts<HelpButton slug="contacts" /></h2>
          <p className="text-sm text-gray-500 mt-1">
            {totalCount === contacts.length && totalPages <= 1
              ? `${totalCount} contacts`
              : `${totalCount} contacts · showing ${contacts.length} on this page`}
          </p>
        </div>
        {canCreate && (
          <button onClick={() => {
              setShowForm(!showForm)
              setEditingContact(null)
              setForm({ ...emptyForm })
              setVendorForm({
                is_1099_vendor: false, vendor_name_override: '', tin_type: 'SSN',
                tin: '', tin_masked: '', w9_on_file: false, w9_received_at: '',
              })
              setVendorSectionOpen(false)
            }}
            className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors">
            + New Contact
          </button>
        )}
      </div>

      {/* Form */}
      {showForm && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-6">
          <h3 className="text-lg font-semibold mb-4">{editingContact ? 'Edit Contact' : 'New Contact'}</h3>
          {!editingContact && contactDraft.banner}
          <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label htmlFor={fid('first-name')} className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
              <input id={fid('first-name')} type="text" required value={form.first_name}
                onChange={e => setForm({...form, first_name: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
            </div>
            <div>
              <label htmlFor={fid('middle-name')} className="block text-sm font-medium text-gray-700 mb-1">Middle Name</label>
              <input id={fid('middle-name')} type="text" value={form.middle_name}
                onChange={e => setForm({...form, middle_name: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
            </div>
            <div>
              <label htmlFor={fid('last-name')} className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
              <input id={fid('last-name')} type="text" required value={form.last_name}
                onChange={e => setForm({...form, last_name: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
            </div>
            <div>
              <label htmlFor={fid('company')} className="block text-sm font-medium text-gray-700 mb-1">Company / Organization</label>
              <input id={fid('company')} type="text" value={form.company_name}
                onChange={e => setForm({...form, company_name: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
            </div>
            <div>
              <label htmlFor={fid('group')} className="block text-sm font-medium text-gray-700 mb-1">Group</label>
              <select id={fid('group')} required value={form.contact_group}
                onChange={e => setForm({...form, contact_group: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500">
                {CONTACT_GROUPS.map(g => (
                  <option key={g.value} value={g.value}>{g.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor={fid('email')} className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input id={fid('email')} type="email" value={form.email}
                onChange={e => setForm({...form, email: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
            </div>
            <div>
              <label htmlFor={fid('phone')} className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
              <input id={fid('phone')} type="text" value={form.phone}
                onChange={e => setForm({...form, phone: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
            </div>
            <div>
              <label htmlFor={fid('secondary-phone')} className="block text-sm font-medium text-gray-700 mb-1">Secondary Phone</label>
              <input id={fid('secondary-phone')} type="text" value={form.secondary_phone}
                onChange={e => setForm({...form, secondary_phone: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
            </div>
            <div>
              <label htmlFor={fid('contact-date')} className="block text-sm font-medium text-gray-700 mb-1">Contact Date</label>
              <input id={fid('contact-date')} type="date" value={form.contact_date}
                onChange={e => setForm({...form, contact_date: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
            </div>
            <div>
              <label htmlFor={fid('address')} className="block text-sm font-medium text-gray-700 mb-1">Address</label>
              <input id={fid('address')} type="text" value={form.address}
                onChange={e => setForm({...form, address: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
            </div>
            <div>
              <label htmlFor={fid('city')} className="block text-sm font-medium text-gray-700 mb-1">City</label>
              <input id={fid('city')} type="text" value={form.city}
                onChange={e => setForm({...form, city: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label htmlFor={fid('state')} className="block text-sm font-medium text-gray-700 mb-1">State</label>
                <input id={fid('state')} type="text" value={form.state}
                  onChange={e => setForm({...form, state: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
              </div>
              <div>
                <label htmlFor={fid('zip')} className="block text-sm font-medium text-gray-700 mb-1">ZIP</label>
                <input id={fid('zip')} type="text" value={form.zip}
                  onChange={e => setForm({...form, zip: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
              </div>
            </div>
            <div className="md:col-span-3">
              <label htmlFor={fid('notes')} className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
              <textarea id={fid('notes')} value={form.notes} rows={2}
                onChange={e => setForm({...form, notes: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
            </div>
            {/* Ship 6e — Tag chips on edit form (mirror Leads drawer UI).
                Show only in edit mode since we need a persisted contact.id
                to attach/detach; new contacts get tags via the Tag page or
                the drawer after save. */}
            {editingContact && (
              <div className="md:col-span-3">
                <label className="block text-xs text-gray-500 uppercase tracking-wider font-medium mb-1">
                  🏷️ Tags
                </label>
                <TagChipsSection
                  tags={editingContactTags}
                  canWrite={
                    (user?.role === 'admin' || user?.permissions?.is_admin) ||
                    Boolean(user?.permissions?.can_edit_contacts)
                  }
                  onAdd={async (tagId) => {
                    await linkContactTag(editingContact, tagId)
                    // Re-fetch the row's tags from the firm catalog we already have
                    const t = allTags.find(x => x.id === tagId)
                    if (t) setEditingContactTags(prev => [...prev, t])
                    fetchContacts()
                  }}
                  onRemove={async (tagId) => {
                    await unlinkContactTag(editingContact, tagId)
                    setEditingContactTags(prev => prev.filter(x => x.id !== tagId))
                    fetchContacts()
                  }}
                />
              </div>
            )}
            {/* Ship 3 — firm-configured custom fields on the contact (edit
                mode only).  Backend PUT requires admin OR can_edit_contacts;
                if the firm hasn't granted can_edit_contacts to anyone, this
                degrades to admin-only per spec §4.13. */}
            {editingContact && (
              <CustomFieldsSection
                entityType="contact"
                entityId={editingContact}
                canWrite={
                  (user?.role === 'admin' || user?.permissions?.is_admin) ||
                  Boolean(user?.permissions?.can_edit_contacts)
                }
              />
            )}
            {/* Ship 7e — 1099 vendor tracking.  Edit-only (need contact.id
                to encrypt + persist TIN).  Perm-gated to admin OR
                can_manage_accounting; server also refuses these fields
                in the PUT body for callers without the perm.
                Note: TIN input never pre-fills from server (only masked
                value shown); auto-save form draft (useFormDraft) NEVER
                receives vendor state so plaintext TIN can't land in
                localStorage. */}
            {editingContact && canManage1099 && (
              <div className="md:col-span-3">
                <details open={vendorSectionOpen}
                  onToggle={(e) => setVendorSectionOpen(e.currentTarget.open)}
                  className="bg-primary-50/60 border border-primary-200 rounded-lg">
                  <summary className="cursor-pointer px-4 py-3 text-sm font-semibold text-primary-900 select-none">
                    📋 1099 Vendor Info
                    {vendorForm.is_1099_vendor && (
                      <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-primary-100 text-primary-800 font-medium">
                        flagged
                      </span>
                    )}
                  </summary>
                  <div className="px-4 py-3 border-t border-primary-100 space-y-3">
                    <label className="flex items-center gap-2 text-sm font-medium text-gray-800">
                      <input type="checkbox"
                        checked={vendorForm.is_1099_vendor}
                        onChange={e => setVendorForm(v => ({ ...v, is_1099_vendor: e.target.checked }))} />
                      Flag this contact as a 1099 vendor
                    </label>
                    {vendorForm.is_1099_vendor && (
                      <>
                        <div>
                          <label htmlFor={fid('vendor-name-override')} className="block text-sm font-medium text-gray-700 mb-1">
                            Vendor name override (optional)
                          </label>
                          <input id={fid('vendor-name-override')} type="text"
                            value={vendorForm.vendor_name_override}
                            onChange={e => setVendorForm(v => ({ ...v, vendor_name_override: e.target.value }))}
                            maxLength={120}
                            placeholder="DBA / filing name if different from contact name"
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label htmlFor={fid('tin-type')} className="block text-sm font-medium text-gray-700 mb-1">
                              TIN type
                            </label>
                            <select id={fid('tin-type')} value={vendorForm.tin_type}
                              onChange={e => setVendorForm(v => ({ ...v, tin_type: e.target.value }))}
                              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500">
                              <option value="SSN">SSN (individual)</option>
                              <option value="EIN">EIN (business)</option>
                              <option value="ITIN">ITIN</option>
                            </select>
                          </div>
                          <div>
                            <label htmlFor={fid('tin')} className="block text-sm font-medium text-gray-700 mb-1">
                              TIN (9 digits)
                            </label>
                            <input id={fid('tin')} type="text"
                              value={vendorForm.tin}
                              onChange={e => setVendorForm(v => ({ ...v, tin: e.target.value }))}
                              placeholder={vendorForm.tin_masked || 'e.g. 123-45-6789'}
                              autoComplete="off"
                              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary-500" />
                            <p className="text-xs text-gray-500 mt-1">
                              {vendorForm.tin_masked
                                ? 'Leave blank to keep existing.'
                                : 'Encrypted at rest; only last 4 digits stored plaintext.'}
                            </p>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <label className="flex items-center gap-2 text-sm font-medium text-gray-800 pt-6">
                            <input type="checkbox"
                              checked={vendorForm.w9_on_file}
                              onChange={e => setVendorForm(v => ({ ...v, w9_on_file: e.target.checked }))} />
                            W-9 on file
                          </label>
                          <div>
                            <label htmlFor={fid('w9-received')} className="block text-sm font-medium text-gray-700 mb-1">
                              W-9 received
                            </label>
                            <input id={fid('w9-received')} type="date"
                              value={vendorForm.w9_received_at}
                              disabled={!vendorForm.w9_on_file}
                              onChange={e => setVendorForm(v => ({ ...v, w9_received_at: e.target.value }))}
                              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50" />
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </details>
              </div>
            )}
            <div className="md:col-span-3 flex gap-3">
              <button type="submit"
                className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-2 rounded-lg text-sm font-medium transition-colors">
                {editingContact ? 'Save Changes' : 'Add Contact'}
              </button>
              <button type="button" onClick={() => {
                  setShowForm(false)
                  setEditingContact(null)
                  setVendorForm({
                    is_1099_vendor: false, vendor_name_override: '', tin_type: 'SSN',
                    tin: '', tin_masked: '', w9_on_file: false, w9_received_at: '',
                  })
                  setVendorSectionOpen(false)
                }}
                className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-6 py-2 rounded-lg text-sm font-medium transition-colors">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4 mb-6">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex-1 min-w-[200px]">
            <input type="text" placeholder="Search by name, company, email, phone..."
              value={search} onChange={e => setSearch(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
          </div>
          <select value={filterGroup} onChange={e => setFilterGroup(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500">
            <option value="">All Groups</option>
            {CONTACT_GROUPS.map(g => (
              <option key={g.value} value={g.value}>{g.label}</option>
            ))}
          </select>
          {/* Ship 7e — 1099 vendors filter chip.  Client-side filter over
              the paginated fetch (backend serializer returns is_1099_vendor
              to every caller — harmless boolean).  Persisted per-user. */}
          <button type="button"
            onClick={() => setOnly1099(v => !v)}
            title={only1099 ? 'Show all contacts' : 'Show only 1099 vendors'}
            className={`text-xs px-3 py-1.5 rounded-full font-medium border transition-colors ${
              only1099
                ? 'bg-primary-600 text-white border-primary-600'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}>
            📋 1099 vendors only
          </button>
        </div>
        {/* Ship 6e — tag filter chips (AND semantic, persisted per user).
            Rendered only when firm has ≥1 tag defined so single-firm setups
            that never use tags don't see empty chrome. */}
        {allTags.length > 0 && (
          <div className="mt-3 pt-3 border-t border-gray-100 flex items-center gap-2 flex-wrap">
            <span className="text-xs text-gray-500 uppercase tracking-wider font-medium mr-1">
              🏷️ Filter by tags:
            </span>
            {allTags.map(tag => {
              const active = tagFilterIds.includes(tag.id)
              const bg = tag.color || '#6366f1'
              const r = parseInt(bg.slice(1, 3), 16), g = parseInt(bg.slice(3, 5), 16), b = parseInt(bg.slice(5, 7), 16)
              const fg = (r * 299 + g * 587 + b * 114) / 1000 >= 140 ? '#111' : '#fff'
              return (
                <button
                  key={tag.id}
                  onClick={() => toggleTagFilter(tag.id)}
                  title={active ? `Remove ${tag.name} from filter` : `Only show contacts tagged ${tag.name}`}
                  className={`text-xs px-2.5 py-0.5 rounded-full font-medium transition-all border ${
                    active
                      ? 'ring-2 ring-offset-1 ring-primary-500 border-transparent'
                      : 'border-gray-200 opacity-70 hover:opacity-100'
                  }`}
                  style={{ backgroundColor: bg, color: fg }}
                >{tag.name}</button>
              )
            })}
            {tagFilterIds.length > 0 && (
              <button
                onClick={clearTagFilter}
                className="text-xs text-gray-500 hover:text-gray-800 ml-1"
                title="Clear tag filter"
              >✕ clear</button>
            )}
            {tagFilterIds.length > 1 && (
              <span className="text-[10px] text-gray-400 italic ml-1">
                (matching ALL {tagFilterIds.length})
              </span>
            )}
          </div>
        )}
      </div>

      {/* Bulk-action bar — renders only when at least one row is selected. */}
      <BulkActionBar
        count={bulk.size}
        onClear={bulk.clear}
        busy={bulkBusy}
        tone="primary"
        actions={[
          ...(canCreate ? [{
            label: 'Change group…',
            icon: '🏷️',
            onClick: () => setBulkGroupOpen(true),
          }] : []),
          ...(canTagContacts ? [{
            label: 'Tag…',
            icon: '🔖',
            onClick: () => { setBulkGroupOpen(false); setBulkTagOpen(v => !v) },
          }] : []),
          ...(canDelete ? [{
            label: 'Delete',
            icon: '🗑️',
            variant: 'danger',
            onClick: handleBulkDelete,
          }] : []),
        ]}
      />

      {bulk.size > 0 && bulkTagOpen && canTagContacts && (
        <BulkTagPicker
          count={bulk.size}
          noun="contact"
          busy={bulkBusy}
          onApply={handleBulkTag}
          onCancel={() => setBulkTagOpen(false)}
        />
      )}

      {/* Bulk-group picker modal — small inline modal for choosing a
          contact_group value.  useAsk() only supports text/textarea/confirm,
          so a bespoke picker is warranted for the enumerated group list. */}
      {bulkGroupOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4"
             onMouseDown={(e) => { if (e.target === e.currentTarget) closeBulkGroup() }}>
          <div {...bulkGroupContainerProps} aria-labelledby="bulk-group-title" className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-5">
            <h3 id="bulk-group-title" className="text-base font-semibold text-gray-800 mb-2">Change contact group</h3>
            <p className="text-sm text-gray-600 mb-3">
              {bulk.size} contact{bulk.size === 1 ? '' : 's'} selected.  Pick the new group:
            </p>
            <label htmlFor="bulk-group-picker" className="sr-only">Contact group</label>
            <select
              id="bulk-group-picker"
              value={bulkGroupValue}
              onChange={(e) => setBulkGroupValue(e.target.value)}
              disabled={bulkBusy}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 mb-4">
              {CONTACT_GROUPS.map(g => (
                <option key={g.value} value={g.value}>{g.label}</option>
              ))}
            </select>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={closeBulkGroup}
                disabled={bulkBusy}
                className="px-4 py-2 text-sm rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50">
                Cancel
              </button>
              <button
                type="button"
                onClick={handleBulkChangeGroup}
                disabled={bulkBusy}
                className="px-4 py-2 text-sm rounded-lg text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50">
                {bulkBusy ? 'Updating…' : `Update ${bulk.size}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
        {filtered.length === 0 ? (
          (() => {
            // "Empty" (no records exist firm-wide) vs "Filtered" (records
            // exist but the current search/group hides them) is decided by
            // whether any filter is active, since `contacts` now only holds
            // the current page.
            const isFiltered = !!(search || filterGroup || tagFilterIds.length > 0)
            return (
              <EmptyState
                icon="📇"
                title={isFiltered ? 'No contacts match your filters' : 'No contacts yet'}
                description={isFiltered
                  ? 'Try clearing the search, group filter, or tag filter.'
                  : 'Contacts are non-clients you interact with: opposing counsel, co-counsel, expert witnesses, mediators, judges, court reporters.  Link them to matters to track relationships.'
                }
                variant={isFiltered ? 'filtered' : 'empty'}
                action={isFiltered
                  ? { label: 'Clear filters', onClick: () => { setSearch(''); setFilterGroup(''); setTagFilterIds([]) } }
                  : (canCreate ? { label: '+ New Contact', onClick: () => { setEditingContact(null); setShowForm(true) } } : undefined)
                }
              />
            )
          })()
        ) : (
          <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                {(canDelete || canCreate) && (
                  <th className="px-3 py-3 w-8">
                    <input
                      type="checkbox"
                      aria-label="Select all visible contacts"
                      checked={bulk.isAllSelected}
                      onChange={(e) => bulk.toggleAll(filtered, e.target.checked)}
                    />
                  </th>
                )}
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase cursor-pointer select-none hover:text-gray-700" onClick={() => sort.toggle('last_name')}>Name <SortIcon dir={sort.key === 'last_name' ? sort.dir : null} /></th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase cursor-pointer select-none hover:text-gray-700" onClick={() => sort.toggle('company')}>Company <SortIcon dir={sort.key === 'company' ? sort.dir : null} /></th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase cursor-pointer select-none hover:text-gray-700" onClick={() => sort.toggle('group')}>Group <SortIcon dir={sort.key === 'group' ? sort.dir : null} /></th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Tags</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase cursor-pointer select-none hover:text-gray-700" onClick={() => sort.toggle('phone')}>Phone <SortIcon dir={sort.key === 'phone' ? sort.dir : null} /></th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase cursor-pointer select-none hover:text-gray-700" onClick={() => sort.toggle('email')}>Email <SortIcon dir={sort.key === 'email' ? sort.dir : null} /></th>
                <th className="text-center px-4 py-3 text-xs font-medium text-gray-500 uppercase">Matters</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map(c => (
                <tr key={c.id} className="hover:bg-gray-50">
                  {(canDelete || canCreate) && (
                    <td className="px-3 py-3 w-8">
                      <input
                        type="checkbox"
                        aria-label={`Select contact ${c.first_name || ''} ${c.last_name || ''}`.trim()}
                        checked={bulk.has(c.id)}
                        onChange={() => bulk.toggle(c.id)}
                      />
                    </td>
                  )}
                  <td className="px-4 py-3 font-medium text-gray-800">
                    {c.first_name} {c.middle_name ? c.middle_name + ' ' : ''}{c.last_name}
                  </td>
                  <td className="px-4 py-3 text-gray-600">{c.company_name || '—'}</td>
                  <td className="px-4 py-3">
                    <InlineSelect
                      value={c.contact_group}
                      options={CONTACT_GROUPS.map((g) => ({ value: g.value, label: g.label }))}
                      canEdit={canCreate}
                      onSave={(v) => saveInlineField(c, 'contact_group', v)}
                      renderValue={(v) => (
                        <span
                          title={`Contact group: ${groupLabel(v)} — click to change`}
                          className={`px-2 py-0.5 rounded-full text-xs font-medium cursor-help ${groupColor(v)}`}>
                          {groupLabel(v)}
                        </span>
                      )}
                    />
                  </td>
                  <td className="px-4 py-3">
                    {Array.isArray(c.tags) && c.tags.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {c.tags.slice(0, 3).map(t => {
                          const bg = t.color || '#6366f1'
                          const r = parseInt(bg.slice(1, 3), 16), g = parseInt(bg.slice(3, 5), 16), b = parseInt(bg.slice(5, 7), 16)
                          const fg = (r * 299 + g * 587 + b * 114) / 1000 >= 140 ? '#111' : '#fff'
                          return (
                            <span key={t.id} className="text-[10px] px-1.5 py-0.5 rounded-full font-medium"
                              style={{ backgroundColor: bg, color: fg }}>{t.name}</span>
                          )
                        })}
                        {c.tags.length > 3 && (
                          <span className="text-[10px] text-gray-500 self-center">+{c.tags.length - 3}</span>
                        )}
                      </div>
                    ) : <span className="text-gray-300 text-xs">—</span>}
                  </td>
                  <td className="px-4 py-3 text-gray-600">
                    <InlineText
                      value={c.phone}
                      canEdit={canCreate}
                      placeholder="—"
                      onSave={(v) => saveInlineField(c, 'phone', v)}
                    />
                  </td>
                  <td className="px-4 py-3 text-gray-600">
                    <InlineText
                      value={c.email}
                      canEdit={canCreate}
                      placeholder="—"
                      onSave={(v) => saveInlineField(c, 'email', v)}
                    />
                  </td>
                  <td className="px-4 py-3 text-center text-gray-500">{parseInt(c.linked_matters) || 0}</td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      {canCreate && (
                        <button onClick={() => handleEdit(c)}
                          className="text-xs text-blue-500 hover:underline">Edit</button>
                      )}
                      {canDelete && (
                        <button onClick={() => handleDelete(c.id)}
                          className="text-xs text-red-400 hover:underline">Delete</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>

      {/* Pager — server-side pagination.  Only renders when there's more
          than one page.  Mirrors the Invoices/Receipts pattern. */}
      {totalPages > 1 && (
        <div className="mt-4 flex items-center justify-between">
          <div className="text-sm text-gray-500">
            Page {page} of {totalPages} · {totalCount} total
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1 || loading}
              className="px-3 py-1.5 text-sm rounded border border-gray-200 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed">
              ← Previous
            </button>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages || loading}
              className="px-3 py-1.5 text-sm rounded border border-gray-200 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed">
              Next →
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
